import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { MapPin, ArrowLeft, Users, Home } from 'lucide-react';
import { PostCard } from '@/components/PostCard';
import { BottomNav } from '@/components/BottomNav';
import { CreatePostModal } from '@/components/CreatePostModal';
import { Button } from '@/components/ui/button';

const CommunityFeedPage = () => {
  const { communityId } = useParams();
  const { user } = useContext(AppContext);
  const navigate = useNavigate();
  const [community, setCommunity] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCommunityData();
    fetchCommunityIdeas();
  }, [communityId]);

  const fetchCommunityData = async () => {
    try {
      const response = await axios.get('/communities');
      const foundCommunity = response.data.find(c => c.id === communityId);
      setCommunity(foundCommunity);
    } catch (error) {
      toast.error('Failed to load community');
    }
  };

  const fetchCommunityIdeas = async () => {
    try {
      const response = await axios.get(`/communities/${communityId}/ideas`);
      setIdeas(response.data);
    } catch (error) {
      toast.error('Failed to load community ideas');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (ideaId, voteType) => {
    try {
      await axios.post(`/ideas/${ideaId}/vote`, { vote_type: voteType });
      fetchCommunityIdeas();
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handleCreatePost = async (formData) => {
    setIsSubmitting(true);
    try {
      // Add community_id to the form data
      const postData = { ...formData, community_id: communityId };
      await axios.post('/ideas', postData);
      toast.success('Local idea posted successfully!');
      setIsModalOpen(false);
      fetchCommunityIdeas();
    } catch (error) {
      toast.error('Failed to post idea');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || !community) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-lg animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface pb-20 md:pb-8">
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="bg-white border-b border-cardBorder sticky top-0 z-40"
      >
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-2 mb-3 -ml-2">
            <button
              onClick={() => navigate('/communities')}
              className="p-2 hover:bg-surface rounded-lg transition-colors"
              title="Back to Communities"
            >
              <ArrowLeft className="w-5 h-5 text-grayDark" />
            </button>
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-surface rounded-lg transition-colors"
              title="Home Feed"
            >
              <Home className="w-5 h-5 text-grayDark" />
            </button>
          </div>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-5 h-5 text-primary" />
                <h1 className="text-2xl font-baloo font-bold text-primaryDark">
                  {community.city}
                </h1>
              </div>
              <p className="text-sm text-grayMeta mb-2">
                {community.state ? `${community.state}, ` : ''}{community.country}
              </p>
              <p className="text-sm text-gray">
                {community.description}
              </p>
            </div>
            <div className="text-right">
              <div className="text-xs text-grayMeta flex items-center gap-1">
                <Users className="w-3 h-3" />
                {community.member_count}
              </div>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Ideas Feed */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {ideas.length === 0 ? (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-sm p-12 text-center border border-cardBorder"
          >
            <div className="w-20 h-20 bg-surface rounded-lg mx-auto mb-4 flex items-center justify-center">
              <MapPin className="w-10 h-10 text-grayMeta" />
            </div>
            <h3 className="font-baloo font-bold text-xl text-primaryDark mb-2">
              No local ideas yet
            </h3>
            <p className="text-gray mb-6">
              Be the first to share a local gem in {community.city}!
            </p>
            <Button
              onClick={() => setIsModalOpen(true)}
              className="bg-primary hover:bg-primary/90 text-primaryDark font-semibold rounded-lg h-12 px-6"
            >
              Share Local Idea
            </Button>
          </motion.div>
        ) : (
          ideas.map((idea, index) => (
            <PostCard
              key={idea.id}
              idea={idea}
              index={index}
              onVote={handleVote}
              currentUser={user}
            />
          ))
        )}
      </main>

      {/* Bottom Nav */}
      <BottomNav onCreateClick={() => setIsModalOpen(true)} />

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreatePost}
        isLoading={isSubmitting}
        communityName={community.city}
      />
    </div>
  );
};

export default CommunityFeedPage;
